import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { useSession } from "@/services/auth0/session";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";

// Types
export interface DepotVolumeData {
  date: string;
  depotId: string | null;
  depotName: string;
  volume: number;
}

export interface CustomerVolumeData {
  date: string;
  customerId: string | null;
  customerName: string;
  volume: number;
}

export interface NewCustomerData {
  date: string;
  count: number;
}

export interface CustomerRevenueData {
  date: string;
  customerId: string | null;
  customerName: string;
  revenue: number;
  orderCount: number;
}

// Helper function to format date range for API queries
export const formatDateRangeParams = (dateRange: DateRange) => {
  const params: Record<string, string | number> = {};
  if (dateRange.from) {
    params.startDate = format(dateRange.from, "yyyy-MM-dd");
  }
  if (dateRange.to) {
    params.endDate = format(dateRange.to, "yyyy-MM-dd");
  }
  return params;
};

// Daily order volume by depot
export const useDailyOrderVolumeByDepot = (
  dateRange: DateRange,
  depotId?: string
) => {
  const { token } = useSession();

  return useQuery<DepotVolumeData[]>({
    queryKey: ["analytics", "dailyOrderVolumeByDepot", dateRange, depotId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (depotId) {
        params.depotId = depotId;
      }

      const response = await axios.get<DepotVolumeData[]>(
        `${apiUrl}/api/v1/analytics/orders/volume/daily/by-depot`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Daily order volume by customer
export const useDailyOrderVolumeByCustomer = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerVolumeData[]>({
    queryKey: [
      "analytics",
      "dailyOrderVolumeByCustomer",
      dateRange,
      customerId,
    ],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<CustomerVolumeData[]>(
        `${apiUrl}/api/v1/analytics/orders/volume/daily/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// New customers daily
export const useNewCustomersDaily = (dateRange: DateRange) => {
  const { token } = useSession();

  return useQuery<NewCustomerData[]>({
    queryKey: ["analytics", "newCustomersDaily", dateRange],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);

      const response = await axios.get<NewCustomerData[]>(
        `${apiUrl}/api/v1/analytics/customers/new/daily`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Daily revenue by customer
export const useDailyRevenueByCustomer = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerRevenueData[]>({
    queryKey: ["analytics", "dailyRevenueByCustomer", dateRange, customerId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<CustomerRevenueData[]>(
        `${apiUrl}/api/v1/analytics/revenue/daily/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Weekly revenue by customer
export const useWeeklyRevenueByCustomer = (
  dateRange: DateRange,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerRevenueData[]>({
    queryKey: ["analytics", "weeklyRevenueByCustomer", dateRange, customerId],
    queryFn: async () => {
      const params = formatDateRangeParams(dateRange);
      if (customerId) {
        params.customerId = customerId;
      }

      const response = await axios.get<CustomerRevenueData[]>(
        `${apiUrl}/api/v1/analytics/revenue/weekly/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (!!dateRange.from || !!dateRange.to) && !!token,
  });
};

// Monthly revenue by customer
export const useMonthlyRevenueByCustomer = (
  year?: number,
  month?: number,
  customerId?: string
) => {
  const { token } = useSession();

  return useQuery<CustomerRevenueData[]>({
    queryKey: [
      "analytics",
      "monthlyRevenueByCustomer",
      year,
      month,
      customerId,
    ],
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (year !== undefined) params.year = year;
      if (month !== undefined) params.month = month;
      if (customerId) params.customerId = customerId;

      const response = await axios.get<CustomerRevenueData[]>(
        `${apiUrl}/api/v1/analytics/revenue/monthly/by-customer`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    },
    enabled: (year !== undefined || month !== undefined) && !!token,
  });
};
